import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { SpotlightContext, type SpotlightContextType } from './useSpotlight';

export const SpotlightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [padding, setPadding] = useState<number>(8);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [text, setText] = useState<ReactNode | undefined>(undefined);

  // Function to focus on an element
  const focus: SpotlightContextType['focus'] = useCallback((element, options) => {
    const bounding = element?.getBoundingClientRect();
    setRect(bounding);
    setText(options?.content);
    setPadding(options?.padding ?? 8);
  }, []);

  // Function to dismiss the spotlight
  const dismiss = useCallback(() => {
    setRect(null);
    setText(undefined);
  }, []);

  return (
    <SpotlightContext.Provider value={{ focus, dismiss }}>
      {children}
      <AnimatePresence>
        {rect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="pointer-events-auto fixed inset-0 z-50"
          >
            <SpotlightOverlay rect={rect} padding={padding} onClose={dismiss} text={text} />
          </motion.div>
        )}
      </AnimatePresence>
    </SpotlightContext.Provider>
  );
};

// The overlay component (now receives rect directly)
const SpotlightOverlay = ({
  rect,
  padding,
  onClose,
  text,
}: {
  rect: DOMRect;
  padding: number;
  onClose: () => void;
  text?: ReactNode;
}) => {
  // Expand the cutout bounds with padding
  const padded = {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    bottom: rect.bottom + padding,
    right: rect.right + padding,
  };

  // Determine the text position based on available space
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  // Check if there's more space above or below the spotlight
  const spaceAbove = padded.top;
  const spaceBelow = viewport.height - padded.bottom;

  // Check if there's more space to the left or right
  const spaceLeft = padded.left;
  const spaceRight = viewport.width - padded.right;

  // Decide where to place the text based on available space
  let textPosition = {};
  let triangleStyle = {};
  const TEXT_MARGIN = 12; // Margin between spotlight and text

  if (spaceBelow >= 60) {
    // Place below
    textPosition = {
      top: padded.bottom + TEXT_MARGIN,
      left: '50%',
      transform: 'translateX(-50%)',
      maxWidth: viewport.width - 30,
    };
    // Triangle pointing up
    triangleStyle = {
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: '8px solid rgba(255, 255, 255, 1)',
      position: 'absolute',
      top: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
    };
  } else if (spaceAbove >= 60) {
    // Place above
    textPosition = {
      bottom: viewport.height - padded.top + TEXT_MARGIN,
      left: '50%',
      transform: 'translateX(-50%)',
      maxWidth: viewport.width - 30,
    };
    // Triangle pointing down
    triangleStyle = {
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderTop: '8px solid rgba(255, 255, 255, 1)',
      position: 'absolute',
      bottom: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
    };
  } else if (spaceRight >= 150) {
    // Place to the right
    textPosition = {
      left: padded.right + TEXT_MARGIN,
      top: padded.top + padded.height / 2,
      transform: 'translateY(-50%)',
      maxWidth: spaceRight - TEXT_MARGIN * 2,
    };
    // Triangle pointing left
    triangleStyle = {
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderRight: '8px solid rgba(255, 255, 255, 1)',
      position: 'absolute',
      left: '-8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
    };
  } else if (spaceLeft >= 150) {
    // Place to the left

    textPosition = {
      right: viewport.width - padded.left + TEXT_MARGIN,
      top: padded.top + padded.height / 2,
      transform: 'translateY(-50%)',
      maxWidth: spaceLeft - TEXT_MARGIN * 2,
    };
    // Triangle pointing right
    triangleStyle = {
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderLeft: '8px solid rgba(255, 255, 255, 1)',
      position: 'absolute',
      right: '-8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
    };
  } else {
    // Fallback: place over the element (semi-transparent)
    textPosition = {
      top: padded.top + padded.height / 2,
      left: padded.left + padded.width / 2,
      transform: 'translate(-50%, -50%)',
      maxWidth: padded.width - 30,
      background: 'rgba(0, 0, 0, 0.7)',
    };
    // No triangle needed when overlapping
    triangleStyle = { display: 'none' };
  }

  return (
    <div className="pointer-events-auto absolute inset-0" onClick={onClose}>
      <div
        className="absolute rounded-full"
        style={{
          top: padded.top,
          left: padded.left,
          width: padded.width,
          height: padded.height,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          pointerEvents: 'none',
        }}
      />

      {text && (
        <>
          <div
            className="bg-color-bg-main pointer-events-none absolute z-50 w-full rounded-lg p-3 text-black"
            style={{
              ...textPosition,
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Triangle pointer */}
            <div style={triangleStyle} />
            <div>{text}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default SpotlightProvider;
